import { ComponentType, CreateComponentRequest } from 'dto/raptor-component.dto';
import * as React from 'react';
import { CheckboxField, showConfirmDialog, Typography } from 'ui-lib';
import { ModalStaticFunctions } from 'ui-lib/components/overlays/modal';
import { ComponentVersionDto } from 'dto/raptor-component-version.dto';
import { ComponentVersionDetails } from './version/component-version-details';

export interface RaptorComponentDeleteOptions {
    componentName: string;
    componentVersion?: string;
    action: (force?: boolean) => Promise<void>;
}

export interface RaptorComponentSelectOption {
    value: ComponentType;
    label: string;
}

export interface RaptorComponentUpdateDescriptionOptions {
    accountId: number;
    componentUuid: string;
    componentName: string;
    description?: string | null;
    onSaved: () => void;
}

export interface RaptorComponentVersionUpdateDescriptionOptions {
    accountId: number;
    componentName: string;
    componentVersion: ComponentVersionDto;
    onSaved: () => void;
}

export interface RaptorComponentVersionDetailsOptions {
    accountId: number;
    componentVersionUuid: string;
}

export class RaptorComponentHelpers {

    public static getComponentTypeOptions(isDPUAdmin: boolean): RaptorComponentSelectOption[] {
        const types = [
            {
                value: ComponentType.DOCKER_IMAGE,
                label: 'Docker Image',
            },
            {
                value: ComponentType.DOCKER_FILE,
                label: 'Docker File',
            },
            {
                value: ComponentType.DOCKER_COMPOSE,
                label: 'Docker Compose',
            },
        ];

        if (isDPUAdmin) {
            return [
                {
                    value: ComponentType.CATAPULT,
                    label: 'Catapult',
                },
                ...types,
            ];
        }

        return types;
    }

    public static toCreateComponentRequest(values: any): CreateComponentRequest {
        return {
            name: values.name,
            description: values.description,
            type: values.type,
        };
    }

    public static showDeleteComponentVersionDialog(options: RaptorComponentDeleteOptions): void {
        let checked = false;
        return showConfirmDialog({
            title: `Delete Component ${options.componentName}${options.componentVersion ? ` Version ${options.componentVersion}` : ''}`,
            content: (
                <div>
                    <Typography.Text>Are you sure you want to delete this component version?</Typography.Text>
                </div>
            ),
            okText: 'OK',
            onOk: async () => {
                await options.action(checked);
            },
        });
    }

    public static showPublishComponentVersionConfirmDialog(options: RaptorComponentDeleteOptions): void {
        return showConfirmDialog({
            title: `Publish Component ${options.componentName} Version ${options.componentVersion}`,
            content: (
                <div>
                    <Typography.Text>Are you sure you want to publish this component version?</Typography.Text>
                </div>
            ),
            okText: 'OK',
            onOk: async () => {
                await options.action();
            },
        });
    }

    public static showDeleteComponentDialog(options: RaptorComponentDeleteOptions): void {
        let checked = false;
        return showConfirmDialog({
            title: `Delete Component ${options.componentName}`,
            content: (
                <div>
                    <Typography.Text>Are you sure you want to delete this component?</Typography.Text>
                    <br />
                    <Typography.Text type='warning'>
                        This will delete all versions of the component and cannot be undone.
                    </Typography.Text>
                    <br />
                    <CheckboxField
                        label={<Typography.Text italic>Force Delete</Typography.Text>}
                        key='force-delete'
                        onChange={(e) => {
                            checked = e.target.checked;
                        }}
                    />
                </div>
            ),
            okText: 'OK',
            onOk: async () => {
                await options.action(checked);
            },
        });
    }

    public static showComponentVersionDetailsModal(
        modal: Omit<ModalStaticFunctions, 'warn'>,
        options: RaptorComponentVersionDetailsOptions,
    ): unknown {
        return modal.info({
            title: 'Component Version Details',
            content: (
                <ComponentVersionDetails
                    accountId={options.accountId}
                    componentVersionUuid={options.componentVersionUuid}
                />
            ),
            width: 1000,
            okText: 'Close',
            cancelButtonProps: { style: { display: 'none' } },
        });
    }
}
